import { defineCollection } from "@rebasepro/common";
/**
 * Every message this app tried to send its operator, and what happened to it.
 *
 * ── Why the row is written BEFORE the send ───────────────────────────────────
 * A notification is a claim about the world ("last night ran, here is what it
 * did"). If the only record of that claim is the delivery attempt, then a
 * Discord outage doesn't just delay the news — it erases it, and the morning
 * looks identical to a morning where nothing needed saying. So the row lands
 * first and the delivery attempt updates it. The database is the record; the
 * channel is just how it reaches a phone.
 *
 * A row stuck in `pending` or `failed` is therefore not junk to clean up: it is
 * the backlog the next run re-sends.
 */
const notificationsCollection = defineCollection({
    name: "Notifications",
    singularName: "Notification",
    slug: "notifications",
    table: "notifications",
    description: "Outbound operator messages. Written before delivery is attempted, so an outage delays the news instead of deleting it.",
    properties: {
        id: { name: "ID", type: "string", isId: "uuid", admin: { readOnly: true } },
        kind: {
            name: "Kind",
            type: "string",
            validation: { required: true },
            defaultValue: "brief",
            enum: [
                { id: "brief", label: "Morning brief", color: "blue" },
                { id: "alert", label: "Alert", color: "red" }
            ]
        },
        status: {
            name: "Status",
            type: "string",
            validation: { required: true },
            defaultValue: "pending",
            enum: [
                { id: "pending", label: "Pending", color: "yellow" },
                { id: "sent", label: "Sent", color: "green" },
                { id: "failed", label: "Delivery failed", color: "red" },
                { id: "unconfigured", label: "No channel configured", color: "gray" }
            ]
        },
        channel: {
            name: "Channel",
            type: "string",
            defaultValue: "discord",
            description: "Where it was addressed. Only a Discord webhook exists today."
        },
        body: {
            name: "Body",
            type: "string",
            validation: { required: true },
            admin: { multiline: true, markdown: true },
            description: "The full message. Readable here whether or not it ever reached a phone."
        },
        fingerprint: {
            name: "Problems reported",
            type: "string",
            description: "Sorted ids of the problems this brief reported. The next brief diffs against it so a standing fault is named once, not every morning until it is fixed.",
            admin: { readOnly: true, hideFromCollection: true }
        },
        attempts: { name: "Attempts", type: "number", defaultValue: 0 },
        error: { name: "Last error", type: "string", admin: { multiline: true } },
        created_at: { name: "Created at", type: "date", autoValue: "on_create", admin: { readOnly: true } },
        sent_at: { name: "Sent at", type: "date" }
    },
    admin: {
        icon: "Notifications",
        group: "Diagnostics",
        defaultViewMode: "table",
        display: {
            title: "kind"
        },
        sort: ["created_at", "desc"],
        propertiesOrder: ["created_at", "kind", "status", "attempts", "sent_at", "body", "error", "channel"]
    }
});
export default notificationsCollection;
